import React, { Fragment, createContext, useContext, useRef } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useFloating, offset, flip, shift, autoUpdate, FloatingPortal } from '@floating-ui/react';
import { cn } from '../../utils/cn';

// Context to share floating refs between trigger and content
const DropdownContext = createContext(null);

/**
 * Dropdown Menu component using Headless UI + Floating UI for portal positioning
 */
export function DropdownMenu({ children }) {
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  return (
    <DropdownContext.Provider value={{ refs, floatingStyles }}>
      <Menu as="div" className="relative inline-block text-left">
        {children}
      </Menu>
    </DropdownContext.Provider>
  );
}

/**
 * Dropdown trigger button
 */
export function DropdownMenuTrigger({ children, className, disabled, ...props }) {
  const context = useContext(DropdownContext);

  return (
    <Menu.Button
      ref={context?.refs.setReference}
      className={className}
      disabled={disabled}
      {...props}
    >
      {children}
    </Menu.Button>
  );
}

/**
 * Dropdown content container - uses Portal to escape overflow:hidden containers
 */
export function DropdownMenuContent({ children, className }) {
  const context = useContext(DropdownContext);

  return (
    <FloatingPortal>
      <Transition
        as={Fragment}
        enter="transition-opacity ease-out duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Menu.Items
          ref={context?.refs.setFloating}
          style={context?.floatingStyles}
          className={cn(
            "z-[9999] w-48 rounded-lg bg-white border border-stone-200 shadow-lg",
            "focus:outline-none py-1",
            className
          )}
        >
          {children}
        </Menu.Items>
      </Transition>
    </FloatingPortal>
  );
}

/**
 * Dropdown menu item
 */
export function DropdownMenuItem({ children, onClick, destructive = false, className }) {
  return (
    <Menu.Item>
      {({ active }) => (
        <button
          onClick={onClick}
          className={cn(
            "flex w-full items-center gap-2 px-3 py-2 text-sm",
            active && "bg-stone-50",
            destructive ? "text-red-600" : "text-stone-700",
            className
          )}
        >
          {children}
        </button>
      )}
    </Menu.Item>
  );
}

/**
 * Dropdown separator
 */
export function DropdownMenuSeparator() {
  return <div className="my-1 h-px bg-stone-200" />;
}
